import { PaginatedResponse } from '@eagle/api-types';
import { getThingFeatureCombinations, MediaSegmentData, MediaType, Thing, ThingType } from '@eagle/core-data-types';
import { VideoSegmentStatus } from '@eagle/data-function-types';
import {
  CacheDataTypes,
  CAMERA_CONTENT_DATE_RANGE,
  EventLocationData,
  FetchOne,
  FetchOneOfAll,
  MediaCard,
  MediaListPageType,
  Undefinable,
  useAuthenticated,
} from '@eagle/react-common';
import { Typography } from '@ethings/ui-toolkit';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toEventTime } from '../trip-history-base-view-utils';

interface TripEventDrawerMediaSectionProps {
  event: EventLocationData;
}

export const TripEventDrawerMediaSection: FC<TripEventDrawerMediaSectionProps> = ({ event }) => {
  const { t } = useTranslation(['track']);
  const { restClient } = useAuthenticated();

  const getMediaDataByFeatureSet = useCallback((cameraFeatures: string[]) => {
    return async (_: Undefinable<string>, mediaType: Undefinable<MediaType>): Promise<PaginatedResponse<MediaSegmentData>> => {
      const status = { status: VideoSegmentStatus.UPLOAD_COMPLETE };
      const occurred = toEventTime(event.occurred).toJSDate();

      return restClient.mediaData.getAllV2({
        filter: {
          '$and': [{
            ...status,
            feature: { '$or': cameraFeatures },
            featureTypeId: 'camera-v0',
            finish: mediaType === MediaType.JPEG ? occurred : { '$gt': occurred },
            mediaType,
            start: mediaType === MediaType.JPEG ? occurred : { '$lt': occurred },
            thingId: event.thingId,
          }],
        },
      });
    };
  }, [event.occurred, event.thingId, restClient.mediaData]);

  if (!event.thingId) {
    return <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.media-unavailable')}</Typography>;
  }

  return (
    <FetchOne
      id={event.thingId}
      dataType={CacheDataTypes.THING}
      renderFactory={({ _id, display, thingTypeId }: Thing) => (
        <FetchOneOfAll
          id={thingTypeId}
          dataType={CacheDataTypes.THING_TYPE}
          renderFactory={({ features }: ThingType) => {
            const cameraFeature = features.find((feature) => feature.featureId === 'camera');
            const cameraFeatures = cameraFeature ? getThingFeatureCombinations(cameraFeature) : [];

            if (!cameraFeatures.length) {
              return <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.no-camera-media')}</Typography>;
            }

            return (
              <MediaCard
                data-testid="trip-event-detail-media"
                entity={{ _id, display }}
                entityTypeFeatures={features}
                eventIndex={0}
                getMediaData={getMediaDataByFeatureSet(cameraFeatures)}
                mediaDaysLimit={CAMERA_CONTENT_DATE_RANGE}
                pageType={MediaListPageType.ALERTS}
              />
            );
          }}
        />
      )}
    />
  );
};
